import React, { useMemo } from 'react';
import classNames from 'classnames';
import moment from 'moment';
import { Tooltip, Checkbox } from 'antd';
import { ApprovalWorkflowItemDto, AWAssetDto } from '@api/Api';
import { formatDuration } from '@helpers/formatDuration';
import { assetPreviewThumbnails } from '@helpers/assetPreviewThumbnails';
import { CheckTaskIcon, LockIcon } from '@icons';
import './ApprovalAssetCard.less';

type ApprovalAssetCardProps = {
  asset: AWAssetDto;
  approvalWorkflow: ApprovalWorkflowItemDto;
  isSelected: boolean;
  onSelect: (asset: AWAssetDto) => void;
  onOpenAsset: (value: {
    approval: ApprovalWorkflowItemDto;
    asset: AWAssetDto;
  }) => void;
};

export default function ApprovalAssetCard({
  asset,
  approvalWorkflow,
  isSelected,
  onOpenAsset,
  onSelect
}: ApprovalAssetCardProps) {
  const disableReason = useMemo(() => {
    if (approvalWorkflow?.isPaused)
      return 'Approval is currently paused. You will be notified when it is resumed.';
    if (approvalWorkflow?.isFinished)
      return 'Approval is already finished. Contact the approval manager if you need to change your decision.';
    if (asset.accessDisabled)
      return 'Your access to this media is restricted. Contact the approval manager if you need to request access.';
    return null;
  }, [
    approvalWorkflow?.isPaused,
    approvalWorkflow?.isFinished,
    asset.accessDisabled
  ]);

  const showLock = !!disableReason;
  const decisionDisabled = showLock || !!asset.decision;
  const grayOut = !!disableReason || decisionDisabled;

  const preparePreview = useMemo(() => {
    const res = assetPreviewThumbnails({
      url: asset.previewUrl || '',
      status: 'ready',
      type: asset.type,
      format: 'img'
    });

    if (typeof res === 'string')
      return <img draggable={false} src={res || ''} alt="" />;
    return res;
  }, [asset]);

  return (
    <Tooltip
      placement="topLeft"
      overlayClassName="approval-asset-card_tooltip"
      title={disableReason}
    >
      <div
        className={classNames('approval-asset-card', {
          'approval-asset-card--disabled': grayOut,
          'approval-asset-card--blur': asset.accessDisabled
        })}
        style={{ cursor: asset.accessDisabled ? 'not-allowed' : undefined }}
        onClick={
          asset.accessDisabled
            ? undefined
            : () => onOpenAsset({ approval: approvalWorkflow, asset })
        }
      >
        <div className="approval-asset-card_preview">
          {showLock && (
            <div className="approval-asset-card_lock-icon">
              <LockIcon />
            </div>
          )}
          {!decisionDisabled && (
            <Checkbox
              value={asset.id}
              checked={isSelected}
              onChange={() => onSelect(asset)}
              onClick={(e) => e.stopPropagation()}
            />
          )}
          <div className="approval-asset-card_image">{preparePreview}</div>
          <div className="approval-asset-card_preview-info">
            <div className="approval-asset-card_preview-info-left">
              {!!asset.decision && (
                <div className="approval-asset-card_preview-info-status">
                  <div
                    className={classNames([
                      'approval-asset-card_preview-info-status-icon',
                      `b-color-${asset.decision}`
                    ])}
                  >
                    <CheckTaskIcon />
                  </div>
                  Already reviewed
                </div>
              )}
            </div>
            {!!asset.duration && (
              <div className="approval-asset-card_preview-info-duration">
                {formatDuration(asset.duration || 0)}
              </div>
            )}
          </div>
        </div>
        <div className="approval-asset-card_content">
          <Tooltip
            placement="topLeft"
            overlayClassName="approval-asset-card_tooltip"
            title={`${asset.name}.${asset.extension}`}
          >
            <div className="approval-asset-card_name">
              {asset.name}.{asset.extension}
            </div>
          </Tooltip>
          <div className="approval-asset-card_date">
            Edited {moment(asset.updatedAt).fromNow()}
          </div>
        </div>
      </div>
    </Tooltip>
  );
}
